import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';
import toast from 'react-hot-toast';
import { FiArrowRight, FiBookOpen, FiExternalLink, FiMail, FiSend, FiAward, FiChevronDown, FiCheckCircle } from 'react-icons/fi';

// Intersection Observer hook for animations
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
};

// Animated section wrapper
const AnimatedSection = ({ children, className = '', id = '' }) => {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      id={id}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.7s ease, transform 0.7s ease',
      }}
    >
      {children}
    </section>
  );
};

// Track visitor
const trackVisit = (page) => {
  try {
    let sessionId = sessionStorage.getItem('visitor-session');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substr(2, 12) + Date.now();
      sessionStorage.setItem('visitor-session', sessionId);
    }
    api.post('/analytics/track', {
      page,
      referrer: document.referrer,
      sessionId
    }).catch(() => {});
  } catch (e) {}
};

const HomePage = () => {
  const [content, setContent] = useState({});
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [imageExists, setImageExists] = useState(false);

  useEffect(() => {
    trackVisit('/');
    fetchContent();
    const img = new Image();
    img.src = '/images/nilesh.png';
    img.onload = () => setImageExists(true);
    img.onerror = () => setImageExists(false);
  }, []);

  const fetchContent = async () => {
    try {
      const res = await api.get('/content');
      const contentMap = {};
      res.data.forEach(item => { contentMap[item.section] = item; });
      setContent(contentMap);
    } catch (err) {
      console.log('Using default content');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setSending(true);
    try {
      await api.post('/contact', formData);
      setMessageSent(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Default data
  const hero = content.hero?.content || {};
  const about = content.about?.content || {};
  const ventures = content.ventures?.content || {};
  const experience = content.experience?.content || {};
  const publications = content.publications?.content || {};
  const books = content.books?.content || {};
  const education = content.education?.content || {};
  const recognition = content.recognition?.content || {};
  const contactInfo = content.contact?.content || {};

  // Fallback defaults
  const venturesList = ventures.ventures || [
    { name: 'ChatWithDr.com', role: 'Founder & CEO', description: 'Online telehealth platform offering $39.99 doctor consultations, same-day prescriptions, lab testing, and Rx savings — serving patients across all 50 states 24/7.', url: 'https://chatwithdr.com' },
    { name: 'DrMedHealth.com', role: 'Founder', description: 'Medical weight loss platform providing GLP-1 prescription medications including Semaglutide and Tirzepatide, with free assessment, provider review, and discreet home delivery.', url: 'https://drmedhealth.com' },
    { name: 'EpiSurveillance.com', role: 'Founder & CEO', description: 'Epidemiological surveillance and data analytics platform for public health monitoring and disease tracking.', url: 'https://episurveillance.com' },
  ];

  const agenciesList = experience.agencies || [
    { name: 'HHS', fullName: 'U.S. Department of Health & Human Services', icon: '🏛️' },
    { name: 'FDA', fullName: 'Food & Drug Administration', icon: '💊' },
    { name: 'DHS', fullName: 'Department of Homeland Security', icon: '🛡️' },
    { name: 'VHA', fullName: 'Veterans Health Administration', icon: '⭐' },
    { name: 'DoD', fullName: 'Department of Defense', icon: '🎖️' },
  ];

  const featuredPubs = publications.featured || [
    { title: 'Incidence of Optic Neuritis and the Associated Risk of Multiple Sclerosis for Service Members of U.S. Armed Forces', journal: 'Military Medicine', date: 'August 2021', url: 'https://academic.oup.com/milmed/article/188/3-4/e697/6356083' },
    { title: 'Sexual Orientation, Body Mass Index, and Methamphetamine Use Among Chicago Youth', journal: 'Walden Dissertations and Doctoral Studies', date: 'Doctoral Dissertation', url: 'https://scholarworks.waldenu.edu/dissertations/8479/' },
  ];

  const otherPubs = publications.other || [
    { title: 'Focused Review – Root Cause Analysis (RCA) Process', date: 'August 2015' },
    { title: 'Sentinel Event Spotlight – "Equipment Issues"', date: 'July 2015' },
    { title: 'Sentinel Event Spotlight – "Communication Failure: A Leading Cause of Wrong Site Surgery"', date: 'August 2014' },
    { title: 'Sentinel Event Spotlight – "Transcription Error"', date: 'July 2014' },
    { title: 'Evaluate Airborne Hazards Open Burn Pit Registry (AHOBPR) Registrants Behavior to Complete the Questionnaire', date: 'September 2019' },
  ];

  const degreesList = education.degrees || [
    { degree: 'DrPH', field: 'Public Health Practice', university: 'Walden University', icon: '🎓' },
    { degree: 'MPH', field: 'Public Health Practice', university: 'Loma Linda University', icon: '📚' },
    { degree: 'MBBS', field: 'Medicine and Surgery', university: 'Veer Narmad South Gujarat University', icon: '⚕️' },
  ];

  const awardsList = recognition.awards || [
    { title: 'Distinguished Performance Award', org: 'DoD Patient Safety Analysis Center', year: '2014', description: 'Recognized for outstanding work and commitment to patient safety.' },
    { title: 'HRSA Honor Award', org: 'Health Resources and Services Administration', year: '2024', description: 'Exceptional contributions to law enforcement collaboration initiatives under the Provider Relief Bureau.' },
  ];

  return (
    <>
      <Navbar />

      {/* ===== HERO SECTION ===== */}
      <section className="hero" id="home">
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">
              <FiAward size={14} />
              Public Health Expert
            </div>
            <h1 className="hero-title">
              {content.hero?.title || 'Dr. Nilesh Panchal'}
            </h1>
            <p className="hero-degrees">
              {content.hero?.subtitle || 'MBBS, MPH, DrPH'}
            </p>
            <p className="hero-description">
              {hero.description || 'Founder & CEO with 22+ years of experience spanning clinical practice and public health, providing expertise at federal agencies including HHS, FDA, DHS, VHA, and DoD.'}
            </p>
            <div className="hero-cta-group">
              <a href="#ventures" className="btn-primary" onClick={(e) => { e.preventDefault(); document.getElementById('ventures')?.scrollIntoView({ behavior: 'smooth' }); }}>
                Explore My Work <FiArrowRight />
              </a>
              <a href="#contact" className="btn-secondary" onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}>
                Get In Touch
              </a>
            </div>
            <div className="hero-stats">
              <div>
                <div className="hero-stat-number">22+</div>
                <div className="hero-stat-label">Years Experience</div>
              </div>
              <div>
                <div className="hero-stat-number">150+</div>
                <div className="hero-stat-label">Books Published</div>
              </div>
              <div>
                <div className="hero-stat-number">5</div>
                <div className="hero-stat-label">Federal Agencies</div>
              </div>
            </div>
          </div>

          <div className="hero-image-wrapper">
            <div className="hero-image-container">
              {imageExists ? (
                <img src="/images/nilesh.png" alt="Dr. Nilesh Panchal" />
              ) : (
                <div className="hero-image-placeholder">
                  <span className="icon">👨‍⚕️</span>
                  <span>Profile Photo</span>
                  <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>Place image at<br />client/public/images/nilesh.png</span>
                </div>
              )}
            </div>
            <div className="hero-float-card books">
              <div className="float-card-number">150+</div>
              <div className="float-card-label">Medical Books</div>
            </div>
            <div className="hero-float-card exp">
              <div className="float-card-number">22+</div>
              <div className="float-card-label">Years in Health</div>
            </div>
          </div>
        </div>

        <div style={{ position: 'absolute', bottom: '30px', left: '50%', transform: 'translateX(-50%)', animation: 'bounce 2s infinite' }}>
          <FiChevronDown size={24} color="var(--text-muted)" />
        </div>
        <style>{`@keyframes bounce { 0%, 100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(8px); } }`}</style>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <AnimatedSection className="section section-alt" id="about">
        <div className="container">
          <div className="section-header">
            <h2>{content.about?.title || 'About Me'}</h2>
            <p>{content.about?.subtitle || 'A Journey of Innovation in Healthcare & Public Health'}</p>
            <div className="section-line"></div>
          </div>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <p style={{ fontSize: '1.08rem', color: 'var(--text-secondary)', lineHeight: 1.85, marginBottom: '32px' }}>
              {about.bio || 'With more than 22 years of experience — 6 years in clinical practice and 16 years in public health practice — I have dedicated my career to improving healthcare outcomes at scale. My expertise spans across federal agencies including the U.S. Department of Health & Human Services (HHS), Food & Drug Administration (FDA), Department of Homeland Security (DHS), Veterans Health Administration (VHA), and Department of Defense (DoD).'}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
              {(about.highlights || [
                '22+ Years of Healthcare & Public Health Experience',
                '6 Years Clinical Practice | 16 Years Public Health',
                'Federal Agency Expertise: HHS, FDA, DHS, VHA, DoD',
                'Author of 150+ Medical Books',
                'Healthcare Technology Entrepreneur'
              ]).map((item, i) => (
                <span key={i} style={{
                  padding: '10px 20px',
                  background: 'var(--accent-glow)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '100px',
                  fontSize: '0.88rem',
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                }}>
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== VENTURES SECTION ===== */}
      <AnimatedSection className="section" id="ventures">
        <div className="container">
          <div className="section-header">
            <h2>{content.ventures?.title || 'Ventures & Leadership'}</h2>
            <p>{content.ventures?.subtitle || 'Building the Future of Healthcare'}</p>
            <div className="section-line"></div>
          </div>
          <div className="ventures-grid">
            {venturesList.map((v, i) => (
              <div key={i} className="card venture-card">
                <h3>{v.name}</h3>
                <div className="role">{v.role}</div>
                <p>{v.description}</p>
                {v.url && (
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="venture-link">
                    Visit Site <FiExternalLink size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FEDERAL EXPERIENCE ===== */}
      <AnimatedSection className="section section-alt" id="experience">
        <div className="container">
          <div className="section-header">
            <h2>{content.experience?.title || 'Federal Experience'}</h2>
            <p>{content.experience?.subtitle || 'Serving at the Highest Levels of Government Healthcare'}</p>
            <div className="section-line"></div>
          </div>
          <div className="agencies-grid">
            {agenciesList.map((a, i) => (
              <div key={i} className="agency-badge">
                <span className="icon">{a.icon}</span>
                <span className="abbr">{a.name}</span>
                <span className="full-name">{a.fullName}</span>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== PUBLICATIONS ===== */}
      <AnimatedSection className="section" id="publications">
        <div className="container">
          <div className="section-header">
            <h2>{content.publications?.title || 'Publications & Research'}</h2>
            <p>{content.publications?.subtitle || 'Contributing to Medical Knowledge'}</p>
            <div className="section-line"></div>
          </div>

          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.3rem', marginBottom: '20px', color: 'var(--accent-primary)' }}>
              Featured Publications
            </h3>
            {featuredPubs.map((pub, i) => (
              <div key={i} className="pub-card">
                <div className="pub-date">{pub.date}</div>
                <div>
                  <div className="pub-title">
                    {pub.url ? (
                      <a href={pub.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                        {pub.title} <FiExternalLink size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />
                      </a>
                    ) : pub.title}
                  </div>
                  {pub.journal && <div className="pub-journal">{pub.journal}</div>}
                </div>
              </div>
            ))}

            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.3rem', marginBottom: '20px', marginTop: '40px', color: 'var(--accent-primary)' }}>
              Other Publications
            </h3>
            {otherPubs.map((pub, i) => (
              <div key={i} className="pub-card">
                <div className="pub-date">{pub.date}</div>
                <div>
                  <div className="pub-title">{pub.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== BOOKS ===== */}
      <AnimatedSection className="section section-alt" id="books">
        <div className="container">
          <div className="books-section-inner">
            <div className="section-header">
              <h2>{content.books?.title || 'Published Books'}</h2>
              <div className="section-line"></div>
            </div>
            <div className="books-count text-gradient">{books.count || '150+'}</div>
            <p style={{ fontSize: '1.3rem', fontFamily: "'Playfair Display', serif", marginBottom: '8px', color: 'var(--text-primary)' }}>
              Medical Books Published
            </p>
            <p>{books.description || 'Authored over 150 medical books covering a wide range of healthcare topics, available on Amazon.'}</p>
            <a
              href={books.amazonUrl || publications.amazonAuthorPage || 'https://www.amazon.com/stores/Dr.-Nilesh-Panchal/author/B08VRJSSS6'}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ marginTop: '10px' }}
            >
              <FiBookOpen /> View on Amazon <FiExternalLink size={14} />
            </a>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== EDUCATION ===== */}
      <AnimatedSection className="section" id="education">
        <div className="container">
          <div className="section-header">
            <h2>{content.education?.title || 'Education'}</h2>
            <p>{content.education?.subtitle || 'Academic Credentials'}</p>
            <div className="section-line"></div>
          </div>
          <div className="edu-timeline">
            {degreesList.map((d, i) => (
              <div key={i} className="edu-card">
                <div className="edu-icon">{d.icon}</div>
                <div>
                  <div className="edu-degree">{d.degree}</div>
                  <div className="edu-field">{d.field}</div>
                  <div className="edu-university">{d.university}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== RECOGNITION ===== */}
      <AnimatedSection className="section section-alt" id="recognition">
        <div className="container">
          <div className="section-header">
            <h2>{content.recognition?.title || 'Awards & Recognition'}</h2>
            <p>{content.recognition?.subtitle || 'Honored for Excellence'}</p>
            <div className="section-line"></div>
          </div>
          <div className="awards-grid">
            {awardsList.map((a, i) => (
              <div key={i} className="card award-card">
                <div className="award-year">
                  <FiAward style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {a.year}
                </div>
                <h3>{a.title}</h3>
                <div className="award-org">{a.org}</div>
                <p>{a.description}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== CONTACT ===== */}
      <AnimatedSection className="section" id="contact">
        <div className="container">
          <div className="section-header">
            <h2>{content.contact?.title || 'Get In Touch'}</h2>
            <p>{content.contact?.subtitle || "Let's Connect"}</p>
            <div className="section-line"></div>
          </div>
          <div className="contact-grid">
            <div className="contact-info">
              <h3>Let's Work Together</h3>
              <p>
                {contactInfo.message || "Whether you're interested in collaborating on healthcare innovation, public health research, or simply want to connect, I'd love to hear from you."}
              </p>
              <a href={`mailto:${contactInfo.email || 'NPanchal@chatwithdr.com'}`} className="contact-email-link">
                <FiMail />
                {contactInfo.email || 'NPanchal@chatwithdr.com'}
              </a>
            </div>

            {/* Show success message OR form */}
            {messageSent ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '48px 32px',
                background: 'var(--bg-tertiary)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
              }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: 'rgba(45, 138, 78, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                }}>
                  <FiCheckCircle size={36} color="var(--success)" />
                </div>
                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.6rem',
                  color: 'var(--text-primary)',
                  marginBottom: '12px',
                }}>
                  Thank You!
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '1.02rem',
                  lineHeight: 1.7,
                  maxWidth: '380px',
                  marginBottom: '24px',
                }}>
                  Your message has been received successfully. I will review it and get back to you as soon as possible.
                </p>
                <button
                  className="btn-secondary"
                  onClick={() => setMessageSent(false)}
                  style={{ fontSize: '0.9rem', padding: '12px 28px' }}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Subject</label>
                  <input
                    type="text"
                    placeholder="What's this about?"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    placeholder="Your message..."
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={sending}>
                  {sending ? 'Sending...' : <>Send Message <FiSend size={16} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FOOTER ===== */}
      <footer className="footer">
        <div className="container">
          <p>© {new Date().getFullYear()} Dr. Nilesh Panchal. All rights reserved.</p>
        </div>
      </footer>
    </>
  );
};

export default HomePage;